import { useEffect, useState } from "react";
import { Menu, X, Wind } from "lucide-react";
import { business } from "@/config/business";
import { scrollToId } from "@/lib/scroll";
import { QuoteButton } from "@/components/CtaButtons";

const NAV_LINKS = [
  { label: "Home", target: "hero" },
  { label: "Layanan", target: "services" },
  { label: "Untuk Rumah", target: "residential" },
  { label: "Untuk Bisnis", target: "industries" },
  { label: "Preventive", target: "preventive-maintenance" },
  { label: "Kontrak", target: "maintenance-contract" },
  { label: "Portfolio", target: "portfolio" },
  { label: "Tentang", target: "why-choose-us" },
  { label: "FAQ", target: "faq" },
  { label: "Kontak", target: "lead-form" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  const go = (target) => {
    setOpen(false);
    setTimeout(() => scrollToId(target), open ? 200 : 0);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-[0_1px_20px_-4px_rgba(15,23,42,0.12)] border-b border-slate-200"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:h-20 lg:px-8">
        {/* Logo */}
        <button
          type="button"
          onClick={() => go("hero")}
          data-testid="navbar-logo"
          className="flex items-center gap-2.5 focus-visible:outline-none"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#0F172A] text-white">
            <Wind className="h-5 w-5" />
          </span>
          <span className="flex flex-col items-start leading-none">
            <span
              className={`text-[16px] font-extrabold tracking-tight ${
                scrolled ? "text-slate-900" : "text-white"
              }`}
            >
              SEJUKARA<span className="text-[#E0A400]">.</span>
            </span>
            <span
              className={`text-[10px] font-semibold uppercase tracking-[0.16em] ${
                scrolled ? "text-slate-400" : "text-slate-300"
              }`}
            >
              by CV Angkasa Briliant
            </span>
          </span>
        </button>

        {/* Desktop nav */}
        <div className="hidden items-center gap-0.5 xl:flex">
          {NAV_LINKS.map((l) => (
            <button
              key={l.target}
              type="button"
              onClick={() => go(l.target)}
              data-testid={`nav-link-${l.target}`}
              className={`rounded-md px-2.5 py-2 text-[13px] font-semibold transition-colors ${
                scrolled
                  ? "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                  : "text-slate-200 hover:text-white hover:bg-white/10"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>

        {/* Desktop CTA */}
        <div className="hidden items-center xl:flex">
          <QuoteButton
            label={business.cta.navbar}
            source="navbar"
            size="sm"
            variant="accent"
            testId="navbar-quote-cta"
            className="!bg-[#25D366] !text-white hover:!bg-[#1eb85a]"
          />
        </div>

        {/* Mobile toggle */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="navbar-mobile-toggle"
          aria-label={open ? "Tutup menu" : "Buka menu"}
          className={`inline-flex h-11 w-11 items-center justify-center rounded-lg xl:hidden ${
            scrolled || open ? "text-slate-900" : "text-white"
          }`}
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {/* Mobile menu */}
      <div
        data-testid="navbar-mobile-menu"
        className={`xl:hidden fixed inset-x-0 top-16 bottom-0 z-40 origin-top bg-white transition-all duration-300 ${
          open ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
        }`}
      >
        <div className="flex h-full flex-col overflow-y-auto px-5 py-6">
          <div className="flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <button
                key={l.target}
                type="button"
                onClick={() => go(l.target)}
                data-testid={`mobile-nav-link-${l.target}`}
                className="rounded-lg px-4 py-3.5 text-left text-lg font-semibold text-slate-800 transition-colors hover:bg-slate-100"
              >
                {l.label}
              </button>
            ))}
          </div>
          <div className="mt-auto pt-6">
            <QuoteButton
              label={business.cta.navbar}
              source="navbar-mobile"
              size="lg"
              variant="accent"
              testId="mobile-navbar-quote-cta"
              className="w-full !bg-[#25D366] !text-white hover:!bg-[#1eb85a]"
            />
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
